import { z } from 'zod'
import { createUpdateSchema } from '@repo/core'

export type AccountType = 'asset' | 'liability' | 'equity' | 'income' | 'expense'
export type AccountSubtype =
  | 'cash_and_bank'
  | 'accounts_receivable'
  | 'allowance_doubtful_accounts'
  | 'inventory'
  | 'prepaid_expenses'
  | 'other_current_asset'
  | 'fixed_asset'
  | 'accumulated_depreciation'
  | 'other_non_current_asset'
  | 'accounts_payable'
  | 'accrued_expenses'
  | 'taxes_payable'
  | 'payroll_liabilities'
  | 'short_term_debt'
  | 'other_current_liability'
  | 'long_term_debt'
  | 'other_non_current_liability'
  | 'owners_capital'
  | 'owners_drawings'
  | 'retained_earnings'
  | 'statutory_reserve'
  | 'operating_revenue'
  | 'service_revenue'
  | 'sales_discount'
  | 'other_income'
  | 'cost_of_goods_sold'
  | 'direct_labor'
  | 'salaries_and_wages'
  | 'rent_expense'
  | 'utilities_expense'
  | 'depreciation_expense'
  | 'office_supplies'
  | 'advertising'
  | 'professional_fees'
  | 'taxes_and_licenses'
  | 'interest_expense'
  | 'bank_service_charges'
  | 'other_expense'

export type NormalBalance = 'debit' | 'credit'
export type EntryType = 'auto' | 'manual' | 'adjusting' | 'closing' | 'reversing'
export type EntryStatus = 'draft' | 'posted' | 'voided'

export interface ChartOfAccount {
  id: string
  tenantId: string
  code: string
  name: string
  accountType: AccountType
  subtype?: AccountSubtype
  normalBalance: NormalBalance
  parentCode?: string
  isHeader?: boolean
  is_active: boolean
  description?: string
  sortOrder?: number
  currency?: string
  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export interface Currency {
  id: string
  code: string // e.g. USD, EUR, PHP
  name: string
  symbol: string
  isBaseCurrency: boolean
  createdAt: number
  updatedAt: number
}

export interface ExchangeRate {
  id: string
  baseCurrency: string
  targetCurrency: string
  rate: number // Target to Base rate multiplier, or Base to Target. Let's say 1 targetCurrency = rate * baseCurrency.
  effectiveDate: number
  createdAt: number
  updatedAt: number
}

export interface JournalEntry {
  id: string
  tenantId: string
  entryDate: number
  referenceNumber: string
  description: string
  entryType: EntryType
  status?: EntryStatus
  sourceTable?: string
  sourceId?: string
  postedBy?: string
  isPosted?: boolean
  totalDebit: number
  totalCredit: number
  fiscalYear?: number
  fiscalMonth?: number
  currency?: string // if different from base PHP
  exchangeRate?: number
  foreignAmount?: number
  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export interface JournalEntryLine {
  id: string
  journalEntryId: string
  accountCode: string
  accountName?: string
  debitAmount?: number
  creditAmount?: number
  description?: string
  businessUnitId?: string
  costCenterId?: string
  moduleSourceTable?: string
  moduleSourceId?: string
  currency?: string
  exchangeRate?: number
  foreignAmount?: number
  createdAt: number
  updatedAt: number
  deletedAt: number | null
}

export type RecurringSchedule = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually'

export interface RecurringJournalTemplate {
  id: string
  tenantId: string
  templateName: string
  schedule: RecurringSchedule
  nextRunDate: number
  endDate?: number
  lines: Array<Omit<JournalEntryLine, 'id' | 'journalEntryId' | 'createdAt' | 'updatedAt' | 'deletedAt'>>
  autoPost: boolean
  isActive: boolean
  lastRunDate?: number
  description?: string
}

export interface InterCompanyTransfer {
  id: string
  sourceTenantId: string
  targetTenantId: string
  amount: number
  currency: string
  description: string
  sourceAccountCode: string
  targetAccountCode: string
  dueFromAccountCode: string
  dueToAccountCode: string
  referenceNumber: string
  status: 'pending' | 'completed' | 'cancelled'
  createdAt: number
}

export interface FinancialPeriod {
  id: string
  tenantId?: string
  fiscalYear: number
  fiscalMonth: number
  periodStart: number
  periodEnd: number
  isClosed: boolean
  closedAt?: number
  closedBy?: string
  createdAt: number
  updatedAt: number
  deletedAt: number | null
}

export interface CostCenter {
  id: string
  tenantId: string
  code: string
  name: string
  parentId?: string
  isActive: boolean
  createdAt: number
  updatedAt: number
}

export type CostCategory = 'Labor' | 'Materials' | 'Subcontractor' | 'Overhead'

export interface JobCostRecord {
  id: string
  tenantId: string
  projectId: string
  costCenterId?: string
  costCategory: CostCategory
  amount: number
  expenseAccountCode: string
  journalEntryId?: string
  description: string
  date: number
}

// ─── AP / AR / Invoicing & Bills ──────────────────────────────

export type SOStatus = 'draft' | 'confirmed' | 'delivered' | 'invoiced' | 'cancelled'
export type POStatus = 'draft' | 'sent' | 'received' | 'billed' | 'cancelled'
export type InvoiceStatus = 'draft' | 'unpaid' | 'partially_paid' | 'paid' | 'overdue' | 'voided'
export type BillStatus = 'draft' | 'pending_approval' | 'approved' | 'partially_paid' | 'paid' | 'overdue' | 'voided'

export interface OrderLineItem {
  id: string
  itemId: string
  description: string
  quantity: number
  unitPrice: number
  discountPercent?: number
  taxRatePercent?: number // typically 12 for VAT
  amount: number
}

export interface SalesOrder {
  id: string
  tenantId: string
  soNumber: string
  customerId: string
  customerName: string
  orderDate: number
  expectedDeliveryDate?: number
  subtotal: number
  taxAmount: number
  totalAmount: number
  status: SOStatus
  notes?: string
  lines: OrderLineItem[]
  createdAt: number
  updatedAt: number
  deletedAt: number | null
}

export interface PurchaseOrder {
  id: string
  tenantId: string
  poNumber: string
  vendorId: string
  vendorName: string
  orderDate: number
  expectedDeliveryDate?: number
  subtotal: number
  taxAmount: number
  totalAmount: number
  status: POStatus
  notes?: string
  lines: OrderLineItem[]
  createdAt: number
  updatedAt: number
  deletedAt: number | null
}

export interface ItemReceipt {
  id: string
  tenantId: string
  receiptNumber: string
  purchaseOrderId: string
  vendorId: string
  vendorName: string
  receiptDate: number
  lines: Array<{
    id: string
    itemId: string
    description: string
    quantityReceived: number
  }>
  createdAt: number
  updatedAt: number
  deletedAt: number | null
}

export interface InvoiceLineItem {
  id: string
  description: string
  quantity: number
  unitPrice: number
  discountPercent?: number
  taxRatePercent?: number
  accountCode: string // Revenue Account Code
  amount: number
}

export interface CustomerInvoice {
  id: string
  tenantId: string
  invoiceNumber: string
  salesOrderId?: string // Link to SO
  birOfficialReceipt?: string // BIR OR number
  customerId: string
  customerName: string
  invoiceDate: number
  dueDate: number
  subtotal: number
  taxAmount: number
  totalAmount: number
  balanceDue: number
  status: InvoiceStatus
  journalEntryId?: string
  notes?: string
  lines: InvoiceLineItem[]
  createdAt: number
  updatedAt: number
  deletedAt: number | null
}

export interface VendorBill {
  id: string
  tenantId: string
  billNumber: string
  purchaseOrderId?: string // Link to PO
  vendorId: string
  vendorName: string
  billDate: number
  dueDate: number
  subtotal: number
  taxAmount: number
  totalAmount: number
  balanceDue: number
  status: BillStatus
  journalEntryId?: string
  notes?: string
  lines: Array<{
    id: string
    description: string
    quantity: number
    unitPrice: number
    accountCode: string // Expense or Inventory Account Code
    amount: number
    withholdingTaxRate?: number // e.g., 1 to 15 for expanded withholding tax
  }>
  createdAt: number
  updatedAt: number
  deletedAt: number | null
}

export interface AgingBucket {
  current: number
  days1To30: number
  days31To60: number
  days61To90: number
  daysOver90: number
  total: number
}

export interface EntityAgingReportItem {
  id: string
  name: string
  aging: AgingBucket
}

// ─── Banking & Reconciliation ──────────────────────────────────

export interface BankTransaction {
  id: string
  tenantId: string
  bankAccountCode: string
  transactionDate: number
  reference: string
  description: string
  amount: number // Positive = deposit/credit, Negative = withdrawal/debit
  matchedJournalLineId?: string
  isCleared: boolean
  clearedDate?: number
  reconciliationId?: string
}

export interface BankReconciliationRecord {
  id: string
  tenantId: string
  bankAccountCode: string
  statementDate: number
  statementEndingBalance: number
  clearedBalance: number
  bookBalance: number
  difference: number
  isReconciled: boolean
  reconciledAt?: number
  reconciledBy?: string
}

// ─── ZOD SCHEMAS ──────────────────────────────────────────────

export const AccountTypeSchema = z.enum(['asset', 'liability', 'equity', 'income', 'expense'])
export const NormalBalanceSchema = z.enum(['debit', 'credit'])
export const EntryTypeSchema = z.enum(['auto', 'manual', 'adjusting', 'closing', 'reversing'])

export const CreateAccountSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1).max(200),
  accountType: AccountTypeSchema,
  subtype: z.string().optional(),
  normalBalance: NormalBalanceSchema,
  parentCode: z.string().optional(),
  isHeader: z.boolean().optional(),
  description: z.string().max(500).optional(),
  sortOrder: z.number().int().optional(),
})

export const CreateJournalEntrySchema = z.object({
  entryDate: z.number().positive(),
  description: z.string().min(1).max(500),
  entryType: EntryTypeSchema.default('manual'),
  lines: z.array(z.object({
    accountCode: z.string().min(1),
    debitAmount: z.number().min(0).optional(),
    creditAmount: z.number().min(0).optional(),
    description: z.string().max(200).optional(),
    costCenterId: z.string().optional(),
  })).min(2, 'Journal entry must have at least 2 lines'),
})

export const UpdateJournalEntrySchema = createUpdateSchema({
  description: z.string().min(1).max(500).optional(),
  isPosted: z.boolean().optional(),
})

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  asset: 'Asset',
  liability: 'Liability',
  equity: 'Equity',
  income: 'Income',
  expense: 'Expense',
}

// ─── Bank CSV Import & Categorization ─────────────────────────

export interface BankImportRule {
  id: string
  tenantId: string
  bankAccountId: string
  matchField: 'description' | 'amount'
  matchPattern: string // regex string
  accountCode: string
  description: string
  priority: number
  isActive: boolean
}

export interface BankImportBatch {
  id: string
  tenantId: string
  bankAccountId: string
  importedAt: number
  rowCount: number
  matchedCount: number
  unmatchedCount: number
  status: 'pending' | 'completed' | 'failed'
}

export type CsvBankTransaction = {
  date: string
  description: string
  amount: number
  reference: string
  rawRow: string
}
